import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, AlertTriangle } from "lucide-react";
import { useGetCycleDetail } from "../../api";
import EventPanel from "@/ml-exports/components/review/event-panel";

/* ═══════════════════════════════════════════════════════════════════
 * CYCLE DETAIL PAGE
 *
 * /machine/cycle/:uuid              → first event with photos
 * /machine/cycle/:uuid?event=<uuid> → specific event
 *
 * Thin wrapper: resolves which event to show, then renders EventPanel.
 * ═══════════════════════════════════════════════════════════════════ */

const isRealEvent = (e: { state: string }) => !e.state.includes("unknown");

const CycleDetailPage = () => {
  const { t } = useTranslation("machines");
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventFromUrl = searchParams.get("event");

  const { data: cycleDetail, isLoading, isError } = useGetCycleDetail(uuid);

  const targetEventUuid = useMemo(() => {
    if (!cycleDetail) return null;

    // If ?event= is specified and exists in this cycle, use it
    if (eventFromUrl) {
      const found = cycleDetail.events.find((e) => e.uuid === eventFromUrl);
      if (found) return found.uuid;
    }

    // Otherwise pick first event with photos, or first real event
    const withPhotos = cycleDetail.events.find(
      (e) => isRealEvent(e) && e.images.length > 0,
    );
    if (withPhotos) return withPhotos.uuid;
    const firstReal = cycleDetail.events.find(isRealEvent);
    return firstReal?.uuid ?? cycleDetail.events[0]?.uuid ?? null;
  }, [cycleDetail, eventFromUrl]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !cycleDetail || !targetEventUuid) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background">
        <div className="p-4 rounded-full bg-muted">
          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">{t("cycleDetail.notFound")}</p>
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-primary underline underline-offset-4"
        >
          {t("actions.goBack")}
        </button>
      </div>
    );
  }

  return (
    <EventPanel
      eventUuid={targetEventUuid}
      onClose={() => navigate(-1)}
      onOpenInDetail={() => {}}
    />
  );
};

export default CycleDetailPage;
